import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Papa from 'papaparse';
import { useData, getCsvUrl } from '../context/DataContext';
import AnimatedNumber from '../components/AnimatedNumber';

const HomePage = () => {
  const { season } = useData();
  const seasonLabel = `${season}/${parseInt(season, 10) + 1}`;

  const [stats, setStats] = useState({ skaters: null, clubs: null, isLoading: true });

  // Charge homme + femme de la saison en cours, indépendamment du filtre du menu,
  // pour afficher un vrai total combiné sur la page d'accueil.
  useEffect(() => {
    let cancelled = false;

    const fetchCombinedStats = async () => {
      setStats((prev) => ({ ...prev, isLoading: true }));
      try {
        const [maleRes, femaleRes] = await Promise.all([
          axios.get(getCsvUrl(season, 'male')),
          axios.get(getCsvUrl(season, 'female')),
        ]);

        const parseRows = (csvText) =>
          Papa.parse(csvText, { header: true, skipEmptyLines: true }).data;

        const maleRows = parseRows(maleRes.data);
        const femaleRows = parseRows(femaleRes.data);
        const allRows = [...maleRows, ...femaleRows];

        const uniqueClubs = new Set(
          allRows.map((row) => row['Club']?.trim()).filter(Boolean)
        );

        if (!cancelled) {
          setStats({
            skaters: allRows.length,
            clubs: uniqueClubs.size,
            isLoading: false,
          });
        }
      } catch (err) {
        console.error('Erreur lors du calcul des statistiques :', err);
        if (!cancelled) {
          setStats({ skaters: null, clubs: null, isLoading: false });
        }
      }
    };

    fetchCombinedStats();
    return () => {
      cancelled = true;
    };
  }, [season]);
  return (
    <div>
      {/* === HERO === */}
      <section className="hero-section">
        <svg
          className="hero-trace"
          viewBox="0 0 1600 500"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            className="hero-trace-line"
            d="M -50 380 C 200 380, 250 120, 480 140 C 700 160, 720 380, 950 360 C 1150 340, 1200 120, 1450 140 C 1550 150, 1620 200, 1680 180"
            fill="none"
          />
          <path
            className="hero-trace-line hero-trace-line--echo"
            d="M -50 400 C 220 400, 260 160, 490 180 C 710 200, 730 400, 960 380 C 1160 360, 1210 160, 1460 180 C 1560 190, 1620 230, 1680 210"
            fill="none"
          />
          <polygon className="hero-shard" points="480,140 486,152 480,164 474,152" />
          <polygon className="hero-shard hero-shard--small" points="950,360 954,368 950,376 946,368" />
        </svg>

        <div className="hero-content">
          <h1 className="hero-title">Le classement patinage, clair et à jour</h1>
          <p className="hero-subtitle">
            Recherche un patineur ou un club, explore les classements par saison,
            et comprends enfin ta feuille de note ISU sans jargon.
          </p>
          <div className="hero-cta-group">
            <Link to="/recherche" className="btn hero-btn-primary">
              Rechercher un patineur
            </Link>
            <Link to="/classement" className="btn hero-btn-secondary">
              Voir le classement général
            </Link>
          </div>
        </div>
      </section>

      {/* === STATS SHOWCASE === */}
      <section className="stats-showcase">
        <div className="container">
          {stats.isLoading ? (
            <p className="stats-loading text-center">Calcul des statistiques...</p>
          ) : stats.skaters !== null ? (
            <div className="row">
              <div className="col-md-4 mb-4">
                <div className="stat-card">
                  <div className="stat-icon-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 17c3-1 3-4.5 6-5.5s4 2 7-1" />
                      <circle cx="17" cy="9.3" r="1.5" fill="currentColor" stroke="none" />
                    </svg>
                  </div>
                  <div className="stat-number">
                    <AnimatedNumber value={stats.skaters} />
                  </div>
                  <div className="stat-label">patineurs classés</div>
                </div>
              </div>

              <div className="col-md-4 mb-4">
                <div className="stat-card">
                  <div className="stat-icon-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                    </svg>
                  </div>
                  <div className="stat-number">
                    <AnimatedNumber value={stats.clubs} />
                  </div>
                  <div className="stat-label">clubs représentés</div>
                </div>
              </div>

              <div className="col-md-4 mb-4">
                <div className="stat-card">
                  <div className="stat-icon-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="16" rx="2" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                      <line x1="8" y1="3" x2="8" y2="7" />
                      <line x1="16" y1="3" x2="16" y2="7" />
                    </svg>
                  </div>
                  <div className="stat-number stat-number--text">{seasonLabel}</div>
                  <div className="stat-label">saison en cours</div>
                </div>
              </div>
            </div>
          ) : (
            <p className="stats-loading text-center">Statistiques indisponibles pour le moment.</p>
          )}
        </div>
      </section>

      {/* === FONCTIONNALITÉS === */}
      <section className="features-showcase">
        <div className="container">
          <div className="features-list">
          <Link to="/recherche" className="feature-row">
            <div className="feature-row-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="10" cy="10" r="6" />
                <line x1="14.5" y1="14.5" x2="20" y2="20" />
              </svg>
            </div>
            <div className="feature-row-content">
              <h3 className="feature-row-title">Recherche instantanée</h3>
              <p className="feature-row-text">
                Trouve un patineur ou un club en tapant quelques lettres, toutes saisons confondues.
              </p>
            </div>
            <div className="feature-row-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </Link>

          <Link to="/classement" className="feature-row">
            <div className="feature-row-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="10" width="4" height="9" rx="1" />
                <rect x="10" y="5" width="4" height="14" rx="1" />
                <rect x="17" y="12" width="4" height="7" rx="1" />
              </svg>
            </div>
            <div className="feature-row-content">
              <h3 className="feature-row-title">Classements à jour</h3>
              <p className="feature-row-text">
                Classement général et par catégorie, réactualisés directement depuis les données de saison.
              </p>
            </div>
            <div className="feature-row-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </Link>

          <Link to="/recherche" className="feature-row feature-row--last">
            <div className="feature-row-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="3" x2="12" y2="21" />
                <line x1="5" y1="7" x2="19" y2="7" />
                <path d="M5 7l-3 6a3 3 0 0 0 6 0l-3-6z" />
                <path d="M19 7l-3 6a3 3 0 0 0 6 0l-3-6z" />
                <line x1="8" y1="21" x2="16" y2="21" />
              </svg>
            </div>
            <div className="feature-row-content">
              <h3 className="feature-row-title">Comparateur</h3>
              <p className="feature-row-text">
                Mets jusqu'à cinq patineurs côte à côte pour comparer scores, médailles et catégories.
              </p>
            </div>
            <div className="feature-row-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </Link>
          </div>
        </div>
      </section>

      {/* === DÉCODEUR DE FEUILLE DE NOTE === */}
      <section className="decode-section">
        <svg
          className="decode-trace"
          viewBox="0 0 1600 400"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            className="decode-trace-line"
            d="M -50 320 C 300 320, 350 80, 650 100 C 950 120, 1000 320, 1300 300 C 1450 290, 1550 150, 1680 160"
            fill="none"
          />
        </svg>

        <div className="container">
          <div className="decode-content">
            <h2 className="decode-title">Comprendre sa feuille de note, enfin</h2>
            <p className="decode-text">
              Chaque élément, chaque GOE, chaque composante expliqués en langage clair —
              pas besoin de connaître le jargon ISU. Cette fonctionnalité est réservée
              aux membres pour l'instant.
            </p>
            {/* ⚠️ Remplace par ton vrai contact (email ou Instagram du club) */}
            <a href="mailto:devflixoise@gmail.com?subject=Demande%20d'accès%20-%20Décode%20ma%20feuille%20de%20note" className="btn decode-btn">
              Demander l'accès
            </a>
          </div>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="site-footer">
        <div className="container text-center">
          <p className="mb-0">
            French Ranking — Saison {seasonLabel} · Données mises à jour depuis les résultats officiels
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
